import React from 'react';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { CHART_COLORS } from '../constants';
import PropTypes from 'prop-types';

const LossTrackingChart = ({ data }) => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;

    // Transform data to include proper week labels and ensure all lines show correctly
    const chartData = data.map(item => ({
        ...item,
        week_display: item.week_label || `W${item.week}`,
        max_possible: item.max_possible_losses,
        projected: item.projected_losses,
        actual: item.actual_losses
    }));

    // Debug log to check data structure (remove in production)
    // console.log('LossTrackingChart data:', chartData.map(d => ({ week: d.week, label: d.week_display, season_type: d.season_type })));

    return (
        <div className="chart-container loss-tracking-chart">
            <h3>League Loss Tracking ($1 per loss)</h3>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                    <XAxis
                        dataKey="week_display"
                        stroke={CHART_COLORS.text}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                        tick={{ fontSize: 11 }}
                    />
                    <YAxis stroke={CHART_COLORS.text} />
                    <Tooltip
                        content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                                const weekLosses = payload[0].payload.week_losses || 0;

                                // Sort payload to match vertical order
                                const sortedPayload = [...payload].sort((a, b) => {
                                    if (a.dataKey === 'max_possible') return -1;
                                    if (b.dataKey === 'max_possible') return 1;
                                    if (a.dataKey === 'projected') return -1;
                                    if (b.dataKey === 'projected') return 1;
                                    return 0;
                                });

                                return (
                                    <div style={{
                                        backgroundColor: CHART_COLORS.background,
                                        border: `1px solid ${CHART_COLORS.grid}`,
                                        color: CHART_COLORS.text,
                                        padding: '10px',
                                        borderRadius: '4px'
                                    }}>
                                        <div style={{ marginBottom: '5px' }}>Week: {label}</div>
                                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#ccc' }}>
                                            Week Losses: {weekLosses}
                                        </div>
                                        {sortedPayload.map((entry, index) => {
                                            let displayName = 'Actual';
                                            if (entry.dataKey === 'max_possible') displayName = 'Max Possible';
                                            else if (entry.dataKey === 'projected') displayName = 'Projected';

                                            return (
                                                <div key={index} style={{
                                                    color: entry.color,
                                                    marginBottom: '2px'
                                                }}>
                                                    {displayName}: ${entry.value}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />
                    <Legend />
                    <Line
                        type="monotone"
                        dataKey="max_possible"
                        stroke="#4a9eff"
                        strokeWidth={2}
                        name="Max Possible"
                        dot={{ fill: '#4a9eff', strokeWidth: 2, r: 4 }}
                    />
                    <Line
                        type="monotone"
                        dataKey="projected"
                        stroke="#ff6b6b"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Projected"
                        dot={{ fill: '#ff6b6b', strokeWidth: 2, r: 4 }}
                    />
                    <Line
                        type="monotone"
                        dataKey="actual"
                        stroke="#82ca9d"
                        strokeWidth={3}
                        name="Actual"
                        dot={{ fill: '#82ca9d', strokeWidth: 2, r: 5 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

LossTrackingChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        week: PropTypes.number,
        week_label: PropTypes.string,
        week_losses: PropTypes.number,
        max_possible_losses: PropTypes.number,
        projected_losses: PropTypes.number,
        actual_losses: PropTypes.number
    }))
};

export default LossTrackingChart;
